import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  TrendingUp, TrendingDown, AlertTriangle, ChevronRight,
  Activity, Droplet, Target, Mail, RefreshCw, Calendar,
  Users, Award, AlertCircle, CheckCircle, Loader2
} from "lucide-react";
import { toast } from "sonner";

const WeeklySummaries = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [complianceFilter, setComplianceFilter] = useState(null);

  useEffect(() => {
    fetchWeeklySummaries();
  }, [token]);

  const fetchWeeklySummaries = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/dietitian/weekly-summaries`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      } else {
        toast.error("Failed to load weekly summaries");
      }
    } catch (error) {
      console.error("Fetch summaries error:", error);
      toast.error("Failed to load weekly summaries");
    } finally {
      setLoading(false);
    }
  };

  const sendPatientSummaryEmail = async (patientId, patientName) => {
    setSendingEmail(patientId);
    try {
      const response = await fetch(`${API}/dietitian/send-weekly-summary/${patientId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success(`Weekly summary sent to ${patientName}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(null);
    }
  };

  const getComplianceColor = (score) => {
    if (score >= 71) return "text-green-600";
    if (score >= 41) return "text-yellow-600";
    return "text-red-600";
  };

  const getComplianceBg = (score) => {
    if (score >= 71) return "bg-green-100";
    if (score >= 41) return "bg-yellow-100";
    return "bg-red-100";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" />
      </div>
    );
  }

  if (!summaryData || summaryData.total_patients === 0) {
    return (
      <Card className="p-8 text-center">
        <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('physician.no_patients_yet_title')}</h3>
        <p className="text-gray-500 mb-4">
          {t('physician.weekly_summaries_desc')}
        </p>
        <Link to="/physician/patients">
          <Button className="bg-[#FF7A00] hover:bg-[#E56900]">
            {t('physician.view_patients')}
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="weekly-summaries">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('physician.weekly_summaries')}</h2>
          <p className="text-sm text-gray-500">
            {summaryData.week_start} - {summaryData.week_end}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchWeeklySummaries}
          className="gap-2"
          data-testid="refresh-summaries"
        >
          <RefreshCw className="w-4 h-4" />
          {t('physician.refresh')}
        </Button>
      </div>

      {/* Overview Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${complianceFilter === null ? 'ring-2 ring-blue-200' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === null ? null : null)}
          data-testid="total-patients-card"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryData.total_patients}</p>
              <p className="text-xs text-gray-500">{t('physician.total_patients')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'high' ? 'ring-2 ring-green-400' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'high' ? null : 'high')}
          data-testid="high-compliance-card"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{summaryData.high_compliance_count}</p>
              <p className="text-xs text-gray-500">{t('physician.high_compliance')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'moderate' ? 'ring-2 ring-yellow-400' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'moderate' ? null : 'moderate')}
          data-testid="moderate-compliance-card"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Activity className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{summaryData.moderate_compliance_count}</p>
              <p className="text-xs text-gray-500">{t('physician.moderate')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${complianceFilter === 'low' ? 'ring-2 ring-red-400' : ''}`}
          onClick={() => setComplianceFilter(complianceFilter === 'low' ? null : 'low')}
          data-testid="low-compliance-card"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{summaryData.low_compliance_count}</p>
              <p className="text-xs text-gray-500">{t('physician.needs_attention')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* HbA1c Average */}
      {summaryData.avg_hba1c && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500 rounded-xl">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Average Estimated HbA1c</p>
                <p className="text-3xl font-bold text-blue-900">{summaryData.avg_hba1c}%</p>
              </div>
            </div>
            <div className="text-right text-sm text-blue-600">
              Across {summaryData.total_patients} patients
            </div>
          </div>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Patients Needing Attention */}
        <Card className="p-4" data-testid="attention-patients">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="font-semibold text-gray-900">{t('physician.needs_attention')}</h3>
          </div>
          
          {summaryData.patients_needing_attention.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <CheckCircle className="w-8 h-8 mx-auto text-green-400 mb-2" />
              <p className="text-sm">All patients are on track!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {summaryData.patients_needing_attention.map((patient) => (
                <div 
                  key={patient.user_id} 
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
                  data-testid={`attention-patient-${patient.user_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{patient.name}</p>
                    <p className="text-xs text-red-600">{patient.reason}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="text-lg font-bold text-red-600">{patient.compliance_score}%</span>
                    <Link to={`/physician/patient/${patient.user_id}`}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top Performers */}
        <Card className="p-4" data-testid="top-performers">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-gray-900">{t('physician.top_performers')}</h3>
          </div>
          
          {summaryData.top_performers.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <Activity className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No high performers this week</p>
            </div>
          ) : (
            <div className="space-y-3">
              {summaryData.top_performers.map((patient, idx) => (
                <div 
                  key={patient.user_id} 
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                  data-testid={`top-performer-${patient.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-green-200 text-green-700 rounded-full text-sm font-bold">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{patient.name}</p>
                      <p className="text-xs text-green-600">{patient.change} from last week</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-green-600">{patient.compliance_score}%</span>
                    <Link to={`/physician/patient/${patient.user_id}`}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Filter Badge */}
      {complianceFilter && (
        <div className="flex items-center gap-2">
          <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1">
            Filtered: {complianceFilter === 'high' ? 'High Compliance' : complianceFilter === 'moderate' ? 'Moderate' : 'Needs Attention'}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setComplianceFilter(null)} className="text-xs text-muted-foreground">
            Clear
          </Button>
        </div>
      )}

      {/* Full Patient List */}
      <Card className="p-4" data-testid="all-patients-summary">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">All Patients Summary</h3>
          <span className="text-sm text-gray-500">{summaryData.patient_summaries.length} patients</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-3 px-2 font-medium text-gray-500">{t('physician.patient')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center">{t('physician.score')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center hidden md:table-cell">{t('physician.meals')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center hidden md:table-cell">{t('physician.glucose_label')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center hidden lg:table-cell">{t('physician.avg_bg')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center hidden lg:table-cell">{t('physician.tir')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center hidden xl:table-cell">{t('physician.est_hba1c')}</th>
                <th className="py-3 px-2 font-medium text-gray-500 text-center">{t('physician.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.patient_summaries
                .filter((p) => {
                  if (!complianceFilter) return true;
                  if (complianceFilter === 'high') return p.compliance_score >= 71;
                  if (complianceFilter === 'moderate') return p.compliance_score >= 41 && p.compliance_score < 71;
                  if (complianceFilter === 'low') return p.compliance_score < 41;
                  return true;
                })
                .sort((a, b) => a.compliance_score - b.compliance_score)
                .map((patient) => (
                <tr 
                  key={patient.user_id} 
                  className="border-b hover:bg-gray-50"
                  data-testid={`patient-row-${patient.user_id}`}
                >
                  <td className="py-3 px-2">
                    <Link 
                      to={`/physician/patient/${patient.user_id}`}
                      className="font-medium text-gray-900 hover:text-[#FF7A00] truncate block max-w-[150px]"
                    >
                      {patient.name}
                    </Link>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getComplianceBg(patient.compliance_score)} ${getComplianceColor(patient.compliance_score)}`}>
                      {patient.compliance_score}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600 hidden md:table-cell">
                    {patient.meals_logged}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600 hidden md:table-cell">
                    {patient.glucose_readings}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600 hidden lg:table-cell">
                    {patient.avg_glucose ? `${patient.avg_glucose}` : "-"}
                  </td>
                  <td className="py-3 px-2 text-center hidden lg:table-cell">
                    {patient.time_in_range ? (
                      <span className={patient.time_in_range >= 70 ? "text-green-600" : "text-yellow-600"}>
                        {patient.time_in_range}%
                      </span>
                    ) : "-"}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600 hidden xl:table-cell">
                    {patient.estimated_hba1c ? `${patient.estimated_hba1c}%` : "-"}
                  </td>
                  <td className="py-3 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => sendPatientSummaryEmail(patient.user_id, patient.name)}
                        disabled={sendingEmail === patient.user_id || !patient.email}
                        title={patient.email ? "Send weekly summary email" : "No email address"}
                        data-testid={`send-email-${patient.user_id}`}
                      >
                        {sendingEmail === patient.user_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                      </Button>
                      <Link to={`/physician/patient/${patient.user_id}`}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default WeeklySummaries;
